import { useState } from 'react';
import Swal from 'sweetalert2';
import { useLocation, useNavigate } from 'react-router-dom';
import EditSharpIcon from '@mui/icons-material/EditSharp';
import { formatSiteNameWithAddress, formatSiteProjectName, getSiteStats, getUpDownKm } from './CustomerMainUtils';
import { getConsigneeSiteDetails } from '../../services/CustomerMainServices';
import CustomerSiteStatsDetailModal from './CustomerSiteStatsDetailModal';
import { CUSTOMER_SITE_DETAIL_ACTIONS } from './customerSiteDetailActions';
import { displayError } from '../../helpers';
import { openPdfResponse } from '../../utils/openPdfInApp';

const STAT_TYPES = {
  no_of_quotation: { type: 'quotation', title: 'Quotation Details' },
  no_of_order: { type: 'order', title: 'Sales Order Details' },
  no_of_schedule: { type: 'schedule', title: 'Work Schedule Details' },
  no_of_dc: { type: 'dc', title: 'List of Delivery Challans' },
};

function CustomerSitesTable({
  siteData,
  onAdd,
  onDelete,
  onEdit,
  showSiteCount = false,
  customerName = '',
  cookies,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [printingRowId, setPrintingRowId] = useState(null);
  const [detailModal, setDetailModal] = useState({
    open: false,
    title: '',
    detailType: '',
    siteLabel: '',
    rows: [],
    loading: false,
  });

  const closeDetailModal = () => {
    setDetailModal({
      open: false,
      title: '',
      detailType: '',
      siteLabel: '',
      rows: [],
      loading: false,
    });
  };

  const openDetailModal = async (site, statKey, count) => {
    const config = STAT_TYPES[statKey];
    if (!config) {
      return;
    }

    if (!site?.id) {
      Swal.fire('Details unavailable', 'Save the customer and site first to view linked records.', 'info');
      return;
    }

    if (!count) {
      setDetailModal({
        open: true,
        title: config.title,
        detailType: config.type,
        siteLabel: formatSiteProjectName(site),
        rows: [],
        loading: false,
      });
      return;
    }

    setDetailModal({
      open: true,
      title: config.title,
      detailType: config.type,
      siteLabel: formatSiteProjectName(site),
      rows: [],
      loading: true,
    });

    try {
      const response = await getConsigneeSiteDetails(cookies, site.id, config.type);
      setDetailModal({
        open: true,
        title: config.title,
        detailType: config.type,
        siteLabel: formatSiteProjectName(site),
        rows: response.detail_list || [],
        loading: false,
      });
    } catch (error) {
      closeDetailModal();
      displayError(error?.response?.data ?? error, 'Failed to load site details');
    }
  };

  const handlePrintRow = async (row) => {
    const actionConfig = CUSTOMER_SITE_DETAIL_ACTIONS[detailModal.detailType];
    if (!actionConfig || !row?.id) {
      return;
    }

    setPrintingRowId(row.id);
    try {
      const response = await actionConfig.print(cookies, row.id);
      openPdfResponse(response, {
        title: actionConfig.printTitle,
        downloadFileName: actionConfig.printFileName,
      });
    } catch (error) {
      displayError(error?.response?.data ?? error, 'Print failed');
    } finally {
      setPrintingRowId(null);
    }
  };

  const handleEditRow = (row) => {
    const actionConfig = CUSTOMER_SITE_DETAIL_ACTIONS[detailModal.detailType];
    if (!actionConfig || !row?.id) {
      return;
    }

    closeDetailModal();
    navigate(actionConfig.editPath(row.id), {
      state: { returnTo: location.pathname },
    });
  };

  const renderStatCell = (site, statKey, value) => {
    const numericValue = Number(value || 0);
    const isClickable = Boolean(site?.id);

    return (
      <button
        type="button"
        className={`customer-site-stat-link${isClickable ? '' : ' is-disabled'}`}
        onClick={() => openDetailModal(site, statKey, numericValue)}
        disabled={!isClickable}
      >
        {numericValue}
      </button>
    );
  };

  return (
    <>
      {showSiteCount && (
        <div className="customer-sites-summary">
          <b>No. of Sites Associated with this Customer: {siteData.length}</b>
        </div>
      )}
      <table className="table customer-sites-table">
        <thead>
          <tr>
            <th>
              <button type="button" className="btn btn-success" onClick={onAdd}>+</button>
            </th>
            <th>Site Name &amp; Address</th>
            <th>Project Name</th>
            <th>Up &amp; Down KM</th>
            <th>No. of Quotation</th>
            <th>No. of Order</th>
            <th>No. Schedules</th>
            <th>No. of DC</th>
            <th>Total Qty</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {siteData.map((site, index) => {
            const stats = getSiteStats(site);
            return (
              <tr key={index}>
                <td>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => onDelete(index)}
                  >
                    x
                  </button>
                </td>
                <td className="customer-site-name-cell">{formatSiteNameWithAddress(site)}</td>
                <td>{site.project_name || ''}</td>
                <td className="text-center">{getUpDownKm(site)}</td>
                <td className="text-center">{renderStatCell(site, 'no_of_quotation', stats.no_of_quotation)}</td>
                <td className="text-center">{renderStatCell(site, 'no_of_order', stats.no_of_order)}</td>
                <td className="text-center">{renderStatCell(site, 'no_of_schedule', stats.no_of_schedule)}</td>
                <td className="text-center">{renderStatCell(site, 'no_of_dc', stats.no_of_dc)}</td>
                <td className="text-center">{Number(stats.total_qty || 0).toFixed(2)}</td>
                <td className="text-center">
                  <EditSharpIcon
                    onClick={() => onEdit(index)}
                    sx={{
                      cursor: 'pointer',
                      fontSize: '24px',
                      color: 'white',
                      backgroundColor: 'blue',
                      padding: '1px',
                      borderRadius: '4px',
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <CustomerSiteStatsDetailModal
        open={detailModal.open}
        title={detailModal.title}
        detailType={detailModal.detailType}
        customerName={customerName}
        siteLabel={detailModal.siteLabel}
        rows={detailModal.rows}
        loading={detailModal.loading}
        printingRowId={printingRowId}
        cookies={cookies}
        onClose={closeDetailModal}
        onPrintRow={handlePrintRow}
        onEditRow={handleEditRow}
      />
    </>
  );
}

export default CustomerSitesTable;
